import type { FieldOpsPerson } from '../domain/fieldOpsPeople';
import { fieldOpsPersonSchema } from '../domain/validation';
import { ApiError, ConflictError, ForbiddenError } from './errors';
import type { FieldOpsPeopleApi, FieldOpsPersonTagsPatch } from './field-ops-people-api';

const SOURCE_UNSET = 'FIELD_WORK_SOURCE_UNSPECIFIED';
const TEAM_UNSET = 'FIELD_OPS_TEAM_UNSPECIFIED';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

function redirectToSignIn(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
}

export class LiveFieldOpsPeopleApi implements FieldOpsPeopleApi {
  async listFieldOpsPeople(): Promise<FieldOpsPerson[]> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}/v1/fieldOpsPeople`, {
        headers: { 'content-type': 'application/json' },
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      const message =
        typeof body.error === 'string'
          ? body.error
          : typeof body.message === 'string'
            ? body.message
            : `Request failed with status ${response.status}`;
      throw new ApiError(message, 'INTERNAL');
    }
    const rows = Array.isArray(body.people) ? body.people : [];
    return rows.map((row) => fieldOpsPersonSchema.parse(row));
  }

  async updateFieldOpsPersonTags(patch: FieldOpsPersonTagsPatch): Promise<FieldOpsPerson> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}/v1/users/${encodeURIComponent(patch.email)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          version: patch.version,
          fieldWorkSource: patch.source ?? SOURCE_UNSET,
          fieldWorkTeam: patch.team ?? TEAM_UNSET,
        }),
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      const message =
        typeof body.error === 'string'
          ? body.error
          : typeof body.message === 'string'
            ? body.message
            : `Request failed with status ${response.status}`;
      if (/version .* does not match/i.test(message)) throw new ConflictError(message);
      if (/not admin|not authorized/i.test(message)) throw new ForbiddenError(message);
      throw new ApiError(message, 'INTERNAL');
    }
    // UpdateUser returns a User; project it back to a FieldOpsPerson.
    return fieldOpsPersonSchema.parse({
      email: body.id,
      displayName: body.displayName ?? '',
      source: body.fieldWorkSource,
      team: body.fieldWorkTeam,
      version: body.version,
    });
  }
}

export const fieldOpsPeopleApi: FieldOpsPeopleApi = new LiveFieldOpsPeopleApi();
